import { camelCase, mapKeys } from 'lodash';

import { streamingEngineServiceAxios } from './interceptor';

export interface StreamingEngineSession {
  sessionId: string;
  channelId: string;
  seSessionId: string;
  seMasterPlaylistUrl: string;
  seMediaPlaylistUrl: string;
  sessionStartedDateUtc: string;
  sessionStoppedDateUtc: string;
  lastReadUtc: string;
  targetDuration: number;
  variants: string[];
  seFetchVariant: string;
  desiredContentSeconds: number;
  enableLiveTracing: boolean;
  isSharedSession?: boolean;
}

export interface UserSessionEnrichedProperties {
  QueryStringParameters: Record<string, string> | null;
  UserId: string | null;
  UserAgent: string | null;
  DeviceType: string | null;
  DeviceIsApp: boolean | null;
  DeviceIsBrowser: boolean | null;
  DeviceIsInAppWebView: boolean | null;
  DeviceIsMobilePhone: boolean | null;
  DeviceIsSetTopBox: boolean | null;
  DeviceIsTV: boolean | null;
  DeviceIsTablet: boolean | null;
  DeviceHardwareType: string | null;
  DeviceOS: string | null;
  DeviceOSFamily: string | null;
  GeoLatitude: number | null;
  GeoLongitude: number | null;
  GeoCountryCode: string | null;
  GeoCountryName: string | null;
  GeoRegionCode: string | null;
  GeoRegionName: string | null;
  GeoCityName: string | null;
  GeoTimeZoneId: string | null;
  GeoTimeZoneCode: string | null;
  GeoZip: string | null;
  GeoType: string | null;
  ApplicationName: string | null;
  ApplicationBundle: string | null;
  OuId: string | null;
  OuName: string | null;
  ToolsChannelId: string | null;
  StreamingChannelId: string | null;
  ChannelName: string | null;
  DeviceFingerprintId: string | null;
  SessionType: string | null;
  VariantsServed: string | null;
  AdsProvider: string | null;
  AdsProviderId: string | null;
  AdBreakDurationSeconds: number | null;
  AccessProductId: string | null;
  AccessContractId: string | null;
  WatchContextChannelId: string | null;
  WatchContextChannelName: string | null;
  WatchContextChannelType: string | null;
  WatchContextReferrerType: string | null;
  WatchContextReferrerId: string | null;
}

export interface UserSession {
  SessionId: string;
  UserSessionDocumentId: string;
  UserSessionId: string;
  UserAgent: string;
  ClientIp: string;
  DeviceFingerprintId: string;
  SessionStartDateUtc: string;
  LastSegmentPlayedDateTimeUtc: string;
  EnableLiveTracing: boolean;
  EnrichedSessionProperties: UserSessionEnrichedProperties;
  /** Playback aggregates from session state (optional for older API responses). */
  TotalAssetsPlayedCount?: number;
  TotalAssetsPlayedDurationSeconds?: number;
  TotalContentPlayedCount?: number;
  TotalContentPlayedDurationSeconds?: number;
  TotalLivePlayedCount?: number;
  TotalLivePlayedDurationSeconds?: number;
  TotalAdsPlayedCount?: number;
  TotalAdsPlayedDurationSeconds?: number;
  TotalAdFillerPlayedCount?: number;
  TotalAdFillerPlayedDurationSeconds?: number;
  TotalBackstopPlayedCount?: number;
  TotalBackstopPlayedDurationSeconds?: number;
  TotalFillerPlayedCount?: number;
  TotalFillerPlayedDurationSeconds?: number;
}

export interface StreamingEngineSessionState {
  sessionId: string;
  isSharedSession: boolean;
  channelId: string;
  seSessionId: string;
  seMasterPlaylistUrl: string;
  seMediaPlaylistUrl: string;
  sessionStartedDateUtc: string;
  sessionStoppedDateUtc: string | null;
  targetDuration: number;
  variants: string[];
  seFetchVariant: string;
  desiredContentSeconds: number;
  enableLiveTracing: boolean;
  liveTracingEnabledDateUtc?: string | null;
}

const extractSessionsArray = (data: unknown): Record<string, unknown>[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const arr = obj.data ?? obj.Data ?? obj.sessions ?? obj.Sessions ?? obj.value ?? obj.Value ?? obj.items ?? obj.Items;
    if (Array.isArray(arr)) return arr;
  }
  return [];
};

const toCamelCaseKeys = (obj: Record<string, unknown>): Record<string, unknown> => {
  return mapKeys(obj, (_, k) => (typeof k === 'string' ? camelCase(k) : String(k)));
};

const StreamingEngineService = {
  async getChannelSessions(channelId: string): Promise<StreamingEngineSession[]> {
    const response = await streamingEngineServiceAxios.get(`admin/channel/${channelId}/sessions`);
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return [];
      }
    }
    const sessions = extractSessionsArray(data);
    try {
      return sessions.map((s: Record<string, unknown>) => toCamelCaseKeys(s) as unknown as StreamingEngineSession);
    } catch (parseErr) {
      console.error('Failed to parse channel sessions response', parseErr, { data, sessions });
      return [];
    }
  },

  async getSessionState(sessionId: string): Promise<StreamingEngineSessionState> {
    const response = await streamingEngineServiceAxios.get(`admin/session/${sessionId}/state`);
    return mapKeys(response.data, (_, k) => camelCase(k)) as StreamingEngineSessionState;
  },

  async setSessionLiveTracing(sessionId: string, enableLiveTracing: boolean): Promise<void> {
    await streamingEngineServiceAxios.patch(`admin/session/${sessionId}/live-tracing`, {
      enableLiveTracing,
    });
  },

  async getUserSession(sessionId: string, userSessionDocumentId: string): Promise<UserSession> {
    const response = await streamingEngineServiceAxios.get(
      `admin/session/${sessionId}/usersession/${userSessionDocumentId}`
    );
    let data = response.data;
    if (typeof data === 'string') {
      data = JSON.parse(data);
    }
    return data as UserSession;
  },

  async getUserSessions(sessionId: string): Promise<UserSession[]> {
    const response = await streamingEngineServiceAxios.get(`admin/session/${sessionId}/usersessions`);
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return [];
      }
    }
    if (Array.isArray(data)) return data as UserSession[];
    return [];
  },

  async setUserSessionLiveTracing(
    sessionId: string,
    userSessionDocumentId: string,
    enableLiveTracing: boolean
  ): Promise<void> {
    await streamingEngineServiceAxios.patch(
      `admin/session/${sessionId}/usersession/${userSessionDocumentId}/live-tracing`,
      { enableLiveTracing }
    );
  },

  async resetSession(sessionId: string): Promise<{ sessionId: string; result: string }> {
    const response = await streamingEngineServiceAxios.post(`admin/session/${sessionId}/reset`);
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return { sessionId, result: data };
      }
    }
    return data as { sessionId: string; result: string };
  },
};

export default StreamingEngineService;
